import { useCallback, useEffect, useMemo, useRef } from "react";
import { useAudioRecorder } from "./audioRecorder";
import {
  analyzeTranscript,
  mergePropositions,
  nextManualId,
  parseSpeakerBlock,
  transcribeChunk,
} from "./recorderService";
import { reportAiUsage } from "../../services/aiUsageService";
import { useAppDispatch, useAppSelector, useAppStore } from "../../store/hooks";
import {
  RECORDER_EMPTY_PANEL,
  selectRecorder,
  selectRecorderOwnerId,
  sessionStarted,
  panelOpenSet,
  analyzingSet,
  errorSet,
  chunkCountSet,
  transcriptLinesAppended,
  analysisApplied,
  propositionItemEdited,
  propositionItemAdded,
  allPropositionsAdded,
  headingEdited,
  reportItemEdited,
  reportItemRemoved,
  freeTextItemAdded,
  includeRevisedToggled,
  resetRecorder,
} from "../../store/slices/recorderSlice";

const EMPTY_VIEW = Object.freeze({ ...RECORDER_EMPTY_PANEL });

/**
 * Consultation-scoped recorder controller.
 *
 * The serializable panel state (transcript / revised transcript / propositions /
 * report) lives in the Redux `recorder` slice as a single slot owned by the
 * appointment that last started recording. It survives navigation (tabs,
 * prev/next appointment, remount on new-appointment creation) and a full browser
 * reload (the slice is mirrored to localStorage), and is only discarded when
 * recording starts for a different appointment.
 *
 * The audio engine stays local: leaving the consultation stops the microphone
 * but never touches the persisted panel.
 */
export function useConsultationRecorder(appointmentId) {
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const slice = useAppSelector(selectRecorder);
  const owns = useAppSelector(selectRecorderOwnerId) === appointmentId;
  const view = owns ? slice : EMPTY_VIEW;

  const queueRef = useRef(Promise.resolve()); // serialize chunk processing in order

  const handleChunkReady = useCallback(
    (blob, index) => {
      const ownerId = appointmentId; // captured now — routes in-flight results
      queueRef.current = queueRef.current.then(async () => {
        try {
          dispatch(analyzingSet({ appointmentId: ownerId, value: true }));
          // Both calls are proxied through the backend, which also logs the
          // real token usage server-side (source: "server") — no client-side
          // reportAiUsage needed here any more (see RecorderAiController).
          const { lines } = await transcribeChunk(blob, `chunk-${index}.webm`, ownerId);
          dispatch(chunkCountSet({ appointmentId: ownerId, count: index + 1 }));
          if (lines.length > 0) {
            dispatch(transcriptLinesAppended({ appointmentId: ownerId, lines }));
          }
          const newText = lines.map((l) => `${l.speaker}: ${l.text}`).join("\n");
          if (!newText.trim()) return;

          const analysis = await analyzeTranscript(newText, ownerId);

          const current = store.getState().recorder;
          if (current.appointmentId !== ownerId) return; // slot claimed elsewhere
          const propositions = mergePropositions(current.propositions, analysis);
          const revisedLines = parseSpeakerBlock(analysis.revised_transcript);
          dispatch(
            analysisApplied({ appointmentId: ownerId, propositions, revisedLines }),
          );
        } catch (err) {
          console.error("[recorder] chunk processing failed:", err);
          dispatch(
            errorSet({
              appointmentId: ownerId,
              message: err.message || "Something went wrong processing that chunk.",
            }),
          );
        } finally {
          dispatch(analyzingSet({ appointmentId: ownerId, value: false }));
        }
      });
    },
    [appointmentId, dispatch, store],
  );

  const handleError = useCallback(
    (err) => {
      console.error("[recorder] mic error:", err);
      dispatch(
        errorSet({
          appointmentId,
          message: err.message || "Could not access the microphone.",
        }),
      );
    },
    [appointmentId, dispatch],
  );

  // Fired once when a recording session fully stops (manual stop, 7-minute cap,
  // or leaving the page). Reports the wall-clock length so the admin dashboard
  // can show per-doctor / per-appointment recorder time. Best-effort only.
  const handleSessionEnd = useCallback(
    (durationSeconds) => {
      if (!durationSeconds || durationSeconds < 1) return;
      reportAiUsage({
        feature: "recorder_session",
        model: "recorder",
        appointmentId,
        durationSeconds,
      });
    },
    [appointmentId],
  );

  const { isRecording, startRecording, stopRecording } = useAudioRecorder({
    onChunkReady: handleChunkReady,
    onError: handleError,
    onSessionEnd: handleSessionEnd,
  });

  const toggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      dispatch(sessionStarted(appointmentId));
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording, dispatch, appointmentId]);

  const setPanelOpen = useCallback(
    (open) => dispatch(panelOpenSet({ appointmentId, open })),
    [dispatch, appointmentId],
  );

  const onEditPropositionItem = useCallback(
    (category, id, text) =>
      dispatch(propositionItemEdited({ appointmentId, category, id, text })),
    [dispatch, appointmentId],
  );

  const onAddPropositionItem = useCallback(
    (category, item) => dispatch(propositionItemAdded({ appointmentId, category, item })),
    [dispatch, appointmentId],
  );

  const onAddAllPropositions = useCallback(
    (category) => dispatch(allPropositionsAdded({ appointmentId, category })),
    [dispatch, appointmentId],
  );

  const onEditHeading = useCallback(
    (heading) => dispatch(headingEdited({ appointmentId, heading })),
    [dispatch, appointmentId],
  );

  const onEditReportItem = useCallback(
    (id, text) => dispatch(reportItemEdited({ appointmentId, id, text })),
    [dispatch, appointmentId],
  );

  const onRemoveReportItem = useCallback(
    (id) => dispatch(reportItemRemoved({ appointmentId, id })),
    [dispatch, appointmentId],
  );

  const onAddFreeTextItem = useCallback(
    (text) => dispatch(freeTextItemAdded({ appointmentId, id: nextManualId(), text })),
    [dispatch, appointmentId],
  );

  const onToggleIncludeRevised = useCallback(
    () => dispatch(includeRevisedToggled({ appointmentId })),
    [dispatch, appointmentId],
  );

  const reset = useCallback(() => {
    stopRecording();
    dispatch(resetRecorder());
  }, [stopRecording, dispatch]);

  // Leaving the consultation (unmount) or switching appointment stops the
  // microphone only — the persisted panel is intentionally kept.
  useEffect(() => {
    return () => {
      stopRecording();
    };
  }, [appointmentId, stopRecording]);

  // Hard reload / tab close: release the microphone (React unmount effects are
  // not guaranteed to run on a full page unload).
  useEffect(() => {
    const onPageHide = () => stopRecording();
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [stopRecording]);

  return useMemo(
    () => ({
      isRecording,
      chunkCount: view.chunkCount,
      isAnalyzing: view.isAnalyzing,
      error: view.error,
      panelOpen: view.panelOpen,
      setPanelOpen,
      sessionActive: view.sessionActive,
      toggleRecording,
      stopRecording, // mic only — keeps the persisted panel (used when leaving the Observations tab)
      reset,

      transcriptLines: view.transcriptLines,
      revisedLines: view.revisedLines,
      propositions: view.propositions,
      report: view.report,
      onEditPropositionItem,
      onAddPropositionItem,
      onAddAllPropositions,
      onEditHeading,
      onEditReportItem,
      onRemoveReportItem,
      onAddFreeTextItem,
      onToggleIncludeRevised,
    }),
    [
      isRecording,
      view,
      setPanelOpen,
      toggleRecording,
      stopRecording,
      reset,
      onEditPropositionItem,
      onAddPropositionItem,
      onAddAllPropositions,
      onEditHeading,
      onEditReportItem,
      onRemoveReportItem,
      onAddFreeTextItem,
      onToggleIncludeRevised,
    ],
  );
}
